import { forwardRef, type MouseEvent as ReactMouseEvent } from 'react';

import { useAppLink } from './hooks/useAppLink.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { Link } from '../../typography/link/Link.js';

const COMPONENT_NAME = 'AppLink';

/**
 * @public
 */
export interface AppLinkProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** The ID of the app to which the link points. */
  appId: string;
  /** ID of the app page to be launched. */
  pageToken?: string;
  /** Function to handle click events on the AppLink. */
  onClick?: (event: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
}

/**
 * `AppLink` renders a link that points to the specified application.
 * @public
 */
export const AppLink = /* @__PURE__ */ forwardRef<
  HTMLAnchorElement,
  AppLinkProps
>((props, forwardedRef) => {
  const {
    appId,
    pageToken,
    children,
    className: consumerClassName,
    style: consumerStyle,
    onClick: consumerOnClick,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const { onClick, href } = useAppLink({ appId, pageToken });

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  return (
    <Link
      {...restProps}
      ref={forwardedRef}
      onClick={(event: ReactMouseEvent<HTMLElement, MouseEvent>) => {
        consumerOnClick?.(event);
        onClick(event);
      }}
      href={href}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </Link>
  );
});

(AppLink as typeof AppLink & { displayName: string }).displayName = 'AppLink';
